import React, { useEffect, useMemo, useRef, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Av, Badge } from '../ui.jsx';

// Searchable contact picker — typeahead on name/phone/email/address. Drops into
// any form that needs to attach a contact. Replaces the giant <Select> dropdown
// that was unusable past ~50 contacts.
//
// Props:
//   value (contactId), onChange({ id, firstName, lastName, phone, email })
//   allowCreate (default true) — shows a "+ Quick add" inline when no result matches
export function ContactPicker({ value, onChange, allowCreate = true, placeholder = 'Search contact by name, phone, email…' }) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContact, setNewContact] = useState({ firstName: '', lastName: '', phone: '', email: '' });
  const wrapRef = useRef(null);

  useEffect(() => { api('/api/contacts').then(setContacts).catch(()=>{}); }, []);

  // Close popup when clicking outside
  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const selected = useMemo(() => contacts.find(c => c.id === value), [contacts, value]);

  const filtered = useMemo(() => {
    if (!q.trim()) {
      // No query → show 10 most recently active
      return contacts.slice(0, 10);
    }
    const term = q.toLowerCase();
    return contacts.filter(c => {
      const hay = [c.firstName, c.lastName, c.phone, c.email, c.address, c.city, c.stage]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    }).slice(0, 20);
  }, [contacts, q]);

  // Return the FULL contact object so downstream forms (estimates, invoices,
  // signature templates) can pull address/city/state/zip into customer-facing
  // documents. Previously only id/name/phone/email — left address fields blank.
  const pick = (c) => {
    onChange(c ? { ...c } : { id: null });
    setQ('');
    setOpen(false);
  };

  const createNew = async () => {
    if (!newContact.firstName || !newContact.phone) {
      alert('First name + phone are required.');
      return;
    }
    setLoading(true);
    try {
      const created = await api('/api/contacts', { method:'POST', body:{
        firstName: newContact.firstName, lastName: newContact.lastName||'',
        phone: newContact.phone, email: newContact.email||'',
        source: 'Manual', stage: 'New Lead',
      }});
      // Server may return a partial response — re-fetch to get the full record.
      const fullContact = created.id ? (await api(`/api/contacts/${created.id}`).catch(() => created)) : created;
      setContacts([fullContact, ...contacts]);
      pick(fullContact);
      setCreating(false);
      setNewContact({ firstName: '', lastName: '', phone: '', email: '' });
    } catch(e) { alert(e.message); } finally { setLoading(false); }
  };

  return <div ref={wrapRef} style={{ position:'relative' }}>
    {selected && !open ? (
      <div onClick={()=>setOpen(true)} style={{ ...styles.inp, display:'flex', alignItems:'center', gap:10, cursor:'pointer', padding:'8px 12px' }}>
        <Av name={`${selected.firstName} ${selected.lastName||''}`} sz={28}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:13 }}>{selected.firstName} {selected.lastName}</div>
          <div style={{ fontSize:11, color:G.textMuted }}>{[selected.phone, selected.email].filter(Boolean).join(' · ')}</div>
        </div>
        <Btn sz="xs" v="ghost" onClick={(e)=>{ e.stopPropagation(); onChange({ id: null }); setOpen(true); }}>✕</Btn>
      </div>
    ) : (
      <input
        type="text"
        value={q}
        onChange={e=>{ setQ(e.target.value); setOpen(true); }}
        onFocus={()=>setOpen(true)}
        placeholder={placeholder}
        style={{ ...styles.inp, width:'100%' }}
        autoComplete="off"
      />
    )}

    {open && !creating && (
      <div style={{
        position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:100,
        ...styles.card, padding:6, maxHeight:340, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,.3)',
      }}>
        {filtered.length === 0 ? (
          <div style={{ padding:12, color:G.textMuted, fontSize:13, textAlign:'center' }}>
            No contacts match "{q}".
            {allowCreate && <div style={{ marginTop:8 }}>
              <Btn sz="sm" v="primary" onClick={()=>{ setCreating(true); const parts = q.split(' '); setNewContact({ ...newContact, firstName: parts[0]||'', lastName: parts.slice(1).join(' ')||'' }); }}>+ Quick add "{q}"</Btn>
            </div>}
          </div>
        ) : (<>
          {!q.trim() && <div style={{ fontSize:10, fontWeight:700, color:G.textDim, textTransform:'uppercase', padding:'6px 10px' }}>Recent contacts</div>}
          {filtered.map(c => (
            // onMouseDown with preventDefault prevents the surrounding input from
            // stealing focus mid-click on mobile/iOS; onClick is what actually picks.
            <div key={c.id}
                 onMouseDown={(e)=>e.preventDefault()}
                 onClick={(e)=>{ e.stopPropagation(); pick(c); }}
                 style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:6, cursor:'pointer' }}
                 onMouseEnter={e=>e.currentTarget.style.background=G.input}
                 onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <Av name={`${c.firstName} ${c.lastName||''}`} sz={28}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <strong style={{ fontSize:13 }}>{c.firstName} {c.lastName}</strong>
                  {c.stage && <Badge c={G.purple} bg={G.purpleDim}>{c.stage}</Badge>}
                </div>
                <div style={{ fontSize:11, color:G.textMuted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {[c.phone, c.email, c.city].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>
          ))}
          {allowCreate && q.trim() && filtered.length > 0 && (
            <div style={{ borderTop:`1px solid ${G.cardBorder}`, marginTop:4, padding:'6px 10px' }}>
              <Btn sz="xs" v="ghost" onClick={()=>{ setCreating(true); const parts = q.split(' '); setNewContact({ ...newContact, firstName: parts[0]||'', lastName: parts.slice(1).join(' ')||'' }); }}>+ Add new contact</Btn>
            </div>
          )}
        </>)}
      </div>
    )}

    {creating && (
      <div style={{
        position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:100,
        ...styles.card, padding:14, boxShadow:'0 8px 24px rgba(0,0,0,.3)',
      }}>
        <div style={{ fontWeight:700, marginBottom:10 }}>+ Quick add contact</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
          <input style={styles.inp} placeholder="First name *" value={newContact.firstName} onChange={e=>setNewContact({...newContact, firstName:e.target.value})}/>
          <input style={styles.inp} placeholder="Last name" value={newContact.lastName} onChange={e=>setNewContact({...newContact, lastName:e.target.value})}/>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
          <input style={styles.inp} placeholder="Phone *" value={newContact.phone} onChange={e=>setNewContact({...newContact, phone:e.target.value})}/>
          <input style={styles.inp} placeholder="Email" value={newContact.email} onChange={e=>setNewContact({...newContact, email:e.target.value})}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
          <Btn sz="sm" v="ghost" onClick={()=>{ setCreating(false); setOpen(true); }}>Back to search</Btn>
          <Btn sz="sm" v="primary" onClick={createNew} disabled={loading}>{loading ? 'Saving…' : 'Save + Pick'}</Btn>
        </div>
      </div>
    )}
  </div>;
}
